import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import API from '../../utils/api';

const Signup = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [colleges, setColleges] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAddCollege, setShowAddCollege] = useState(false);
  const [showAddDepartment, setShowAddDepartment] = useState(false);
  const [newCollege, setNewCollege] = useState({ name: '', abbreviation: '' });
  const [newDepartment, setNewDepartment] = useState('');

  const [formData, setFormData] = useState({
    school_id: '', first_name: '', last_name: '', middle_name: '',
    email: '', phone: '', facebook: '', gender: '',
    password: '', confirm_password: '',
    college_id: '', department_id: '',
    emergency_contact_name: '', emergency_contact_relationship: '',
    emergency_contact_phone: '',
  });

  useEffect(() => {
    fetchColleges();
  }, []);

  const fetchColleges = async () => {
    try {
      const res = await API.get('/auth/colleges');
      setColleges(res.data);
    } catch (err) {
      console.error('Error fetching colleges:', err);
    }
  };

  const fetchDepartments = async (college_id) => {
    try {
      const res = await API.get(`/auth/departments/${college_id}`);
      setDepartments(res.data);
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    if (e.target.name === 'college_id') {
      setFormData(prev => ({ ...prev, college_id: e.target.value, department_id: '' }));
      if (e.target.value === 'other') {
        setShowAddCollege(true);
      } else {
        setShowAddCollege(false);
        fetchDepartments(e.target.value);
      }
    }
    if (e.target.name === 'department_id' && e.target.value === 'other') {
      setShowAddDepartment(true);
    } else if (e.target.name === 'department_id') {
      setShowAddDepartment(false);
    }
  };

  const handleAddCollege = async () => {
    if (!newCollege.name || !newCollege.abbreviation) {
      setError('Please enter college name and abbreviation!');
      return;
    }
    try {
      const res = await API.post('/auth/colleges', newCollege);
      setColleges(res.data.colleges);
      const added = res.data.colleges.find(c => c.abbreviation === newCollege.abbreviation);
      setFormData(prev => ({ ...prev, college_id: added.id.toString() }));
      setShowAddCollege(false);
      setNewCollege({ name: '', abbreviation: '' });
      fetchDepartments(added.id);
    } catch (err) {
      setError('Error adding college!');
    }
  };

  const handleAddDepartment = async () => {
    if (!newDepartment) {
      setError('Please enter department name!');
      return;
    }
    try {
      const res = await API.post('/auth/departments', {
        name: newDepartment,
        college_id: formData.college_id
      });
      setDepartments(res.data.departments);
      const added = res.data.departments.find(d => d.name === newDepartment);
      setFormData(prev => ({ ...prev, department_id: added.id.toString() }));
      setShowAddDepartment(false);
      setNewDepartment('');
    } catch (err) {
      setError('Error adding department!');
    }
  };

  const validateStep1 = () => {
    if (!formData.school_id || !formData.first_name || !formData.last_name ||
      !formData.email || !formData.phone || !formData.gender) {
      setError('Please fill in all required fields!');
      return false;
    }
    if (!formData.email.includes('@')) {
      setError('Please enter a valid email!');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.password || !formData.confirm_password) {
      setError('Please fill in all required fields!');
      return false;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters!');
      return false;
    }
    if (formData.password !== formData.confirm_password) {
      setError('Passwords do not match!');
      return false;
    }
    if (!formData.college_id) {
      setError('Please select your college!');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.emergency_contact_name || !formData.emergency_contact_relationship ||
      !formData.emergency_contact_phone) {
      setError('Please fill in all emergency contact fields!');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    setError('');
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep3()) return;
    setLoading(true);
    try {
      await API.post('/auth/signup', formData);
      setSuccess('✅ Signup successful! Please wait for admin approval.');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong!');
    }
    setLoading(false);
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.title}>DULIMBAY</h1>
          <p style={styles.subtitle}>Create your account</p>
        </div>

        {/* Step indicator */}
        <div style={styles.stepIndicator}>
          {[1, 2, 3].map(s => (
            <div key={s} style={styles.stepWrapper}>
              <div style={s <= step ? styles.stepActive : styles.stepInactive}>
                {s}
              </div>
              {s < 3 && <div style={s < step ? styles.lineActive : styles.lineInactive} />}
            </div>
          ))}
        </div>
        <div style={styles.stepLabels}>
          <span style={styles.stepLabel}>Personal Info</span>
          <span style={styles.stepLabel}>Account</span>
          <span style={styles.stepLabel}>Emergency</span>
        </div>

        {error && <div style={styles.error}>{error}</div>}
        {success && <div style={styles.successMsg}>{success}</div>}

        <form onSubmit={handleSubmit}>
          {/* STEP 1 - Personal Info */}
          {step === 1 && (
            <div>
              <div style={styles.row}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>School ID <span style={styles.required}>*</span></label>
                  <input style={styles.input} type="text" name="school_id"
                    placeholder="ex. 211-01312" value={formData.school_id} onChange={handleChange} />
                    <small style={styles.hint}>
                    Students: 211-01312 · Instructors: 2026-11111
                    </small>
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Gender <span style={styles.required}>*</span></label>
                  <select style={styles.input} name="gender" value={formData.gender} onChange={handleChange}>
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div style={styles.row}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>First Name <span style={styles.required}>*</span></label>
                  <input style={styles.input} type="text" name="first_name"
                    placeholder="First name" value={formData.first_name} onChange={handleChange} />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Last Name <span style={styles.required}>*</span></label>
                  <input style={styles.input} type="text" name="last_name"
                    placeholder="Last name" value={formData.last_name} onChange={handleChange} />
                </div>
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Middle Name <span style={styles.optional}>(optional)</span></label>
                <input style={styles.input} type="text" name="middle_name"
                  placeholder="Middle name" value={formData.middle_name} onChange={handleChange} />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Email <span style={styles.required}>*</span></label>
                <input style={styles.input} type="email" name="email"
                  placeholder="your@email.com" value={formData.email} onChange={handleChange} />
              </div>
              <div style={styles.row}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Phone <span style={styles.required}>*</span></label>
                  <input style={styles.input} type="text" name="phone"
                    placeholder="09XXXXXXXXX" value={formData.phone} onChange={handleChange} />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Facebook <span style={styles.optional}>(optional)</span></label>
                  <input style={styles.input} type="text" name="facebook"
                    placeholder="Facebook name" value={formData.facebook} onChange={handleChange} />
                </div>
              </div>
              <button type="button" style={styles.button} onClick={handleNext}>
                Next →
              </button>
            </div>
          )}

          {/* STEP 2 - Account & College */}
          {step === 2 && (
            <div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Password <span style={styles.required}>*</span></label>
                <input style={styles.input} type="password" name="password"
                  placeholder="At least 6 characters" value={formData.password} onChange={handleChange} />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Confirm Password <span style={styles.required}>*</span></label>
                <input style={styles.input} type="password" name="confirm_password"
                  placeholder="Repeat your password" value={formData.confirm_password} onChange={handleChange} />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>College <span style={styles.required}>*</span></label>
                <select style={styles.input} name="college_id" value={formData.college_id} onChange={handleChange}>
                  <option value="">Select college</option>
                  {colleges.map(c => (
                    <option key={c.id} value={c.id}>{c.abbreviation} - {c.name}</option>
                  ))}
                  <option value="other">+ Add my college</option>
                </select>
              </div>
              {showAddCollege && (
                <div style={styles.addBox}>
                  <p style={styles.addTitle}>Add New College</p>
                  <input style={styles.input} type="text" placeholder="Full college name"
                    value={newCollege.name} onChange={e => setNewCollege({ ...newCollege, name: e.target.value })} />
                  <input style={{ ...styles.input, marginTop: '8px' }} type="text"
                    placeholder="Abbreviation (ex. CCIS)"
                    value={newCollege.abbreviation} onChange={e => setNewCollege({ ...newCollege, abbreviation: e.target.value })} />
                  <button type="button" style={styles.addButton} onClick={handleAddCollege}>Add College</button>
                </div>
              )}
              {formData.college_id && formData.college_id !== 'other' && (
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Department</label>
                  <select style={styles.input} name="department_id" value={formData.department_id} onChange={handleChange}>
                    <option value="">Select department</option>
                    {departments.map(d => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                    <option value="other">+ Add my department</option>
                  </select>
                </div>
              )}
              {showAddDepartment && (
                <div style={styles.addBox}>
                  <p style={styles.addTitle}>Add New Department</p>
                  <input style={styles.input} type="text" placeholder="Full department name"
                    value={newDepartment} onChange={e => setNewDepartment(e.target.value)} />
                  <button type="button" style={styles.addButton} onClick={handleAddDepartment}>Add Department</button>
                </div>
              )}
              <div style={styles.buttonRow}>
                <button type="button" style={styles.backButton} onClick={() => setStep(1)}>← Back</button>
                <button type="button" style={styles.button} onClick={handleNext}>Next →</button>
              </div>
            </div>
          )}

          {/* STEP 3 - Emergency Contact */}
          {step === 3 && (
            <div>
              <p style={styles.sectionNote}>In case of emergency, who should we contact?</p>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Full Name <span style={styles.required}>*</span></label>
                <input style={styles.input} type="text" name="emergency_contact_name"
                  placeholder="Emergency contact full name"
                  value={formData.emergency_contact_name} onChange={handleChange} />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Relationship <span style={styles.required}>*</span></label>
                <input style={styles.input} type="text" name="emergency_contact_relationship"
                  placeholder="ex. Mother, Father, Sibling"
                  value={formData.emergency_contact_relationship} onChange={handleChange} />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Phone Number <span style={styles.required}>*</span></label>
                <input style={styles.input} type="text" name="emergency_contact_phone"
                  placeholder="09XXXXXXXXX"
                  value={formData.emergency_contact_phone} onChange={handleChange} />
              </div>
              <div style={styles.buttonRow}>
                <button type="button" style={styles.backButton} onClick={() => setStep(2)}>← Back</button>
                <button type="submit" style={loading ? styles.buttonDisabled : styles.button} disabled={loading}>
                  {loading ? 'Signing up...' : 'Sign Up'}
                </button>
              </div>
            </div>
          )}
        </form>

        <p style={styles.loginText}>
          Already have an account?{' '}
          <Link to="/login" style={styles.link}>Sign In</Link>
        </p>
      </div>
    </div>
  );
};

const styles = {
    hint: {
    color: '#aaa',
    fontSize: '11px',
    marginTop: '4px',
    display: 'block'
    },
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    padding: '20px',
  },
  card: {
    background: 'white',
    borderRadius: '20px',
    padding: '40px',
    width: '100%',
    maxWidth: '500px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  header: { textAlign: 'center', marginBottom: '20px' },
  title: { fontSize: '28px', fontWeight: 'bold', color: '#0f3460', margin: '0', letterSpacing: '3px' },
  subtitle: { color: '#666', margin: '4px 0', fontSize: '14px' },
  stepIndicator: { display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px' },
  stepWrapper: { display: 'flex', alignItems: 'center' },
  stepActive: { width: '32px', height: '32px', borderRadius: '50%', background: '#0f3460', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '14px' },
  stepInactive: { width: '32px', height: '32px', borderRadius: '50%', background: '#ddd', color: '#999', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '14px' },
  lineActive: { width: '60px', height: '3px', background: '#0f3460', margin: '0 4px' },
  lineInactive: { width: '60px', height: '3px', background: '#ddd', margin: '0 4px' },
  stepLabels: { display: 'flex', justifyContent: 'space-between', marginBottom: '20px', padding: '0 10px' },
  stepLabel: { fontSize: '11px', color: '#888' },
  error: { background: '#ffe0e0', color: '#cc0000', padding: '12px', borderRadius: '8px', marginBottom: '15px', fontSize: '14px', textAlign: 'center' },
  successMsg: { background: '#e0ffe0', color: '#006600', padding: '12px', borderRadius: '8px', marginBottom: '15px', fontSize: '14px', textAlign: 'center' },
  row: { display: 'flex', gap: '12px' },
  inputGroup: { marginBottom: '14px', flex: 1 },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '5px' },
  required: { color: 'red' },
  optional: { color: '#aaa', fontWeight: 'normal', fontSize: '11px' },
  input: { width: '100%', padding: '11px 14px', borderRadius: '10px', border: '1.5px solid #ddd', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
  button: { width: '100%', padding: '13px', background: 'linear-gradient(135deg, #0f3460, #16213e)', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer', marginTop: '10px' },
  buttonDisabled: { width: '100%', padding: '13px', background: '#ccc', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', cursor: 'not-allowed', marginTop: '10px' },
  buttonRow: { display: 'flex', gap: '12px', marginTop: '10px' },
  backButton: { flex: 1, padding: '13px', background: 'white', color: '#0f3460', border: '2px solid #0f3460', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer' },
  addBox: { background: '#f8f9fa', borderRadius: '10px', padding: '15px', marginBottom: '14px' },
  addTitle: { fontWeight: 'bold', color: '#333', margin: '0 0 10px 0', fontSize: '14px' },
  addButton: { marginTop: '10px', padding: '10px 20px', background: '#0f3460', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' },
  sectionNote: { color: '#666', fontSize: '13px', marginBottom: '15px' },
  loginText: { textAlign: 'center', marginTop: '20px', fontSize: '14px', color: '#666' },
  link: { color: '#0f3460', fontWeight: 'bold', textDecoration: 'none' },
};

export default Signup;